import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { tasksAPI } from '../lib/api';
import { formatDate, formatDateTime, getDaysUntil, getStatusColor, getStatusLabel } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { 
  ClipboardList, 
  Clock,
  CheckCircle2,
  AlertTriangle,
  Calendar,
  Loader2,
  ArrowRight,
  Play,
  MapPin,
  Timer
} from 'lucide-react';
import Layout from '../components/Layout';
import { TranslatableSpan } from '../components/TranslatableText';

const TechnicianDashboard = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    assigned: 0,
    inProgress: 0,
    completedToday: 0,
    overdue: 0
  });

  const fetchMyTasks = useCallback(async () => {
    try {
      const res = await tasksAPI.getAll();
      // Filter tasks assigned to current user
      const myTasks = res.data.filter(task => task.assigned_to === user?.id);
      setTasks(myTasks);
      
      // Calculate stats
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const assigned = myTasks.filter(t => t.status === 'assigned').length;
      const inProgress = myTasks.filter(t => t.status === 'in_progress').length;
      const completedToday = myTasks.filter(t => {
        if (t.status !== 'completed') return false;
        const completedDate = new Date(t.updated_at);
        completedDate.setHours(0, 0, 0, 0);
        return completedDate.getTime() === today.getTime();
      }).length;
      const overdue = myTasks.filter(t => {
        if (t.status === 'completed') return false;
        if (!t.deadline) return false;
        return new Date(t.deadline) < new Date();
      }).length;
      
      setStats({ assigned, inProgress, completedToday, overdue });
    } catch (err) {
      console.error('Failed to fetch tasks:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchMyTasks();
  }, [fetchMyTasks]);

  const getActiveTasks = () => {
    return tasks
      .filter(t => t.status !== 'completed' && t.status !== 'reported')
      .sort((a, b) => {
        // Sort by priority first (critical > high > medium > low)
        const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
        const priorityDiff = (priorityOrder[a.priority] || 2) - (priorityOrder[b.priority] || 2);
        if (priorityDiff !== 0) return priorityDiff;
        
        // Then by deadline
        if (a.deadline && b.deadline) {
          return new Date(a.deadline) - new Date(b.deadline);
        }
        return a.deadline ? -1 : 1;
      });
  };

  const getUpcomingDeadlines = () => {
    return tasks
      .filter(t => t.deadline && t.status !== 'completed')
      .sort((a, b) => new Date(a.deadline) - new Date(b.deadline))
      .slice(0, 5);
  };

  const startWork = async (taskId) => {
    try {
      await tasksAPI.update(taskId, { status: 'in_progress' });
      fetchMyTasks();
    } catch (err) {
      console.error('Failed to start work:', err);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
        </div>
      </Layout>
    );
  }

  const activeTasks = getActiveTasks();
  const upcomingDeadlines = getUpcomingDeadlines();

  return (
    <Layout>
      <div className="space-y-6" data-testid="technician-dashboard">
        {/* Header */}
        <div>
          <h1 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold uppercase tracking-tight text-white">
            My Work Orders
          </h1>
          <p className="text-slate-400 mt-1">
            Welcome back, {user?.name}
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="card-industrial" data-testid="stat-assigned">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-slate-400 uppercase text-xs tracking-wider font-mono">Assigned</p>
                <p className="text-3xl font-bold text-blue-400 mt-1">{stats.assigned}</p>
              </div>
              <div className="w-10 h-10 bg-blue-500/20 rounded-sm flex items-center justify-center">
                <ClipboardList className="w-5 h-5 text-blue-400" />
              </div>
            </div>
          </div>

          <div className="card-industrial" data-testid="stat-in-progress">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-slate-400 uppercase text-xs tracking-wider font-mono">In Progress</p>
                <p className="text-3xl font-bold text-yellow-400 mt-1">{stats.inProgress}</p>
              </div>
              <div className="w-10 h-10 bg-yellow-500/20 rounded-sm flex items-center justify-center">
                <Clock className="w-5 h-5 text-yellow-400" />
              </div>
            </div>
          </div>

          <div className="card-industrial" data-testid="stat-completed-today">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-slate-400 uppercase text-xs tracking-wider font-mono">Done Today</p>
                <p className="text-3xl font-bold text-green-400 mt-1">{stats.completedToday}</p>
              </div>
              <div className="w-10 h-10 bg-green-500/20 rounded-sm flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-green-400" />
              </div>
            </div>
          </div>

          <div className="card-industrial" data-testid="stat-overdue">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-slate-400 uppercase text-xs tracking-wider font-mono">Overdue</p>
                <p className="text-3xl font-bold text-red-400 mt-1">{stats.overdue}</p>
              </div>
              <div className="w-10 h-10 bg-red-500/20 rounded-sm flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
            </div>
          </div>
        </div>

        {/* Overdue Alert */}
        {stats.overdue > 0 && (
          <div className="card-industrial border-red-500/50 bg-red-500/5">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              <p className="text-red-400 font-medium">
                You have {stats.overdue} overdue work order{stats.overdue > 1 ? 's' : ''} that need attention!
              </p>
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-6">
          {/* Active Work Orders */}
          <div className="card-industrial">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight text-white flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-amber-500" />
                Active Work Orders
              </h2>
              <Link 
                to="/tasks" 
                className="text-amber-500 hover:text-amber-400 text-sm flex items-center gap-1"
              >
                View All <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            
            {activeTasks.length > 0 ? (
              <div className="space-y-3">
                {activeTasks.slice(0, 5).map((task) => {
                  const isOverdue = task.deadline && new Date(task.deadline) < new Date() && task.status !== 'completed';
                  
                  return (
                    <div
                      key={task.id}
                      className="p-4 bg-slate-700/50 border border-slate-600 rounded-sm hover:border-amber-500/50 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`priority-indicator ${task.priority}`}></span>
                            <Link 
                              to={`/tasks/${task.id}`}
                              className="text-white font-medium hover:text-amber-400 truncate"
                            >
                              <TranslatableSpan text={task.title} />
                            </Link>
                          </div>
                          <div className="flex flex-wrap items-center gap-3 text-sm text-slate-400">
                            {task.location && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {task.location}
                              </span>
                            )}
                            {task.deadline && (
                              <span className={`flex items-center gap-1 ${isOverdue ? 'text-red-400' : ''}`}>
                                <Calendar className="w-3 h-3" />
                                {formatDate(task.deadline)}
                                {isOverdue && <span className="font-mono text-xs">(OVERDUE)</span>}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`status-badge ${getStatusColor(task.status)}`}>
                            {getStatusLabel(task.status)}
                          </span>
                          {task.status === 'assigned' && (
                            <Button
                              size="sm"
                              onClick={() => startWork(task.id)}
                              className="btn-primary h-8 px-3"
                              data-testid={`start-work-${task.id}`}
                            >
                              <Play className="w-3 h-3 mr-1" />
                              Start
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="empty-state py-8">
                <CheckCircle2 className="w-12 h-12 text-green-500 mb-3" />
                <p className="text-slate-400">No active work orders!</p>
                <p className="text-green-400 text-sm mt-1">You're all caught up</p>
              </div>
            )}
          </div>

          {/* Upcoming Deadlines */}
          <div className="card-industrial">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight text-white flex items-center gap-2">
                <Timer className="w-5 h-5 text-amber-500" />
                Upcoming Deadlines
              </h2>
            </div>
            
            {upcomingDeadlines.length > 0 ? (
              <div className="space-y-3">
                {upcomingDeadlines.map((task) => {
                  const daysUntil = getDaysUntil(task.deadline);
                  const isOverdue = daysUntil < 0;
                  
                  return (
                    <Link
                      key={task.id}
                      to={`/tasks/${task.id}`}
                      className="block p-3 bg-slate-700/50 border border-slate-600 rounded-sm hover:border-amber-500/50 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium truncate"><TranslatableSpan text={task.title} /></p>
                          <p className="text-slate-400 text-sm mt-1">
                            {formatDate(task.deadline)}
                          </p>
                        </div>
                        <span className={`text-xs font-mono px-2 py-1 rounded-sm ${
                          isOverdue ? 'bg-red-500/20 text-red-400' : 
                          daysUntil <= 1 ? 'bg-amber-500/20 text-amber-400' :
                          daysUntil <= 3 ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-slate-600 text-slate-400'
                        }`}>
                          {isOverdue ? 'OVERDUE' : daysUntil === 0 ? 'TODAY' : daysUntil === 1 ? '1 DAY' : `${daysUntil} DAYS`}
                        </span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            ) : (
              <div className="empty-state py-8">
                <Calendar className="w-12 h-12 text-slate-600 mb-3" />
                <p className="text-slate-400">No upcoming deadlines</p>
              </div>
            )}
          </div>
        </div>

        {/* Recent Completed */}
        {tasks.filter(t => t.status === 'completed').length > 0 && (
          <div className="card-industrial">
            <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight text-white flex items-center gap-2 mb-4">
              <CheckCircle2 className="w-5 h-5 text-green-500" />
              Recently Completed
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {tasks
                .filter(t => t.status === 'completed')
                .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
                .slice(0, 6)
                .map((task) => (
                  <Link
                    key={task.id}
                    to={`/tasks/${task.id}`}
                    className="p-3 bg-green-500/10 border border-green-500/30 rounded-sm hover:border-green-500/50 transition-colors"
                  >
                    <p className="text-white font-medium truncate"><TranslatableSpan text={task.title} /></p>
                    <p className="text-green-400 text-xs mt-1">
                      Completed {formatDateTime(task.updated_at)}
                    </p>
                  </Link>
                ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default TechnicianDashboard;
